import React, { useEffect, useState } from 'react';
import {
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Box,
  SelectChangeEvent,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { ReactComponent as OrderHubLogo } from '../../../assets/svgs/Orderhub-logo.svg';
import { Cell } from '@m4a3/api-client';
import { defaultTimezone } from '../../../utils/date-utils';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { getEnvironment, getColourForEnvironment } from '../../../services/environment';
import { getHeaderBackgroundColor } from '../../../utils/ui';

interface OrderHubHeaderProps {
  selectedCellId: number
  handleCellUpdate: (event: SelectChangeEvent<number>) => void;
  cells: Cell[]
  refreshInterval: number
  handleRefreshTimeChange: (event: SelectChangeEvent<number>) => void;
  timeFilter: number;
  handleSinceMinutesQuery: (event: SelectChangeEvent<number>) => void;
  onSearchButtonClicked?: () => void;
  useSSEForOrders?: boolean;
  isConnected?: boolean;
  showCompletedOrders?: boolean;
  onToggleCompletedOrders?: (show: boolean) => void;
}

export const OrderHubHeader = ({
  selectedCellId,
  handleCellUpdate,
  cells,
  refreshInterval,
  handleRefreshTimeChange,
  timeFilter,
  handleSinceMinutesQuery,
  onSearchButtonClicked,
  useSSEForOrders = false,
  isConnected = false,
  showCompletedOrders = false,
  onToggleCompletedOrders,
  }: OrderHubHeaderProps) => {
  const REFRESH_INTERVAL_OPTIONS = [5, 10, 30];
  const [currentCell, setCurrentCell] = useState<Cell>();

  useEffect(() => {
    setCurrentCell(cells?.find(cell => cell.id === selectedCellId));
  }, [cells, selectedCellId]);

  const getEnvironmentBadgeIfNotProd = (): JSX.Element => {
    const environment = getEnvironment();

    if (['dev', 'uat'].includes(environment)) {
      return (<div className='absolute -top-3 -right-3 z-40 overflow-visible'>
        <span className={`inline-flex items-center rounded-full bg-${getColourForEnvironment()}-100 px-2 py-1 text-xs font-medium text-${getColourForEnvironment()}-800 border border-1 border-${getColourForEnvironment()}-600 capitalize`}>
          {environment}
        </span>
      </div>)
    }

    return <></>;
  }

  return (<div className={`${getHeaderBackgroundColor()} pt-2`}>
    <div className="flex p-2">
      <div className="w-15 h-full flex items-center justify-start relative mt-0.5 mr-2">
        <OrderHubLogo />
        {getEnvironmentBadgeIfNotProd()}
      </div>
      <div className="flex w-[240px] justify-between">
        <Select
          fullWidth
          value={selectedCellId}
          onChange={handleCellUpdate}>
          {cells?.sort((a, b) => a.displayName.localeCompare(b.displayName))
            .map(c => {
            return (
              <MenuItem key={`cell-${c.id}`} value={c.id}>
                {c.displayName || c.cellName}
              </MenuItem>
            );
          })}
        </Select>
      </div>

      {!useSSEForOrders && (
        <div className="flex pl-2 w-[160px]">
          <FormControl fullWidth className={'w-full'}>
            <InputLabel>Refresh Time</InputLabel>
            <Select
              value={refreshInterval}
              onChange={handleRefreshTimeChange}
              label="Refresh Time">
              {REFRESH_INTERVAL_OPTIONS.map(seconds => {
                const displayTime =
                  seconds === 5
                    ? '5 seconds (MC only)'
                    : `${seconds} seconds`;
                return (
                  <MenuItem key={`time-${seconds}`} value={seconds}>
                    {displayTime}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
      )}
      
      {!useSSEForOrders && (
        <div className="flex pl-2 w-[160px]">
          <FormControl fullWidth className={'w-full'}>
            <InputLabel>Orders Within Last: </InputLabel>
            <Select
              value={timeFilter}
              onChange={handleSinceMinutesQuery}
              label="Orders Within Last: ">
              <MenuItem key={`30`} value={30}>
                30 mins
              </MenuItem>
              <MenuItem key={`60`} value={60}>
                1 hour
              </MenuItem>
              <MenuItem key={`120`} value={120}>
                2 hours
              </MenuItem>
              <MenuItem key={`300`} value={300}>
                5 hours
              </MenuItem>
              <MenuItem key={`720`} value={720}>
                12 hours
              </MenuItem>
              <MenuItem key={`1440`} value={1440}>
                24 hours
              </MenuItem>
            </Select>
          </FormControl>
        </div>
      )}
      
      {useSSEForOrders && isConnected && (
        <div className="flex pl-2 items-center">
          <div className="flex items-center px-4">
            <span className="relative flex h-3 w-3 mr-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
            </span>
            <Typography variant="body1" className="text-green-700 font-medium px-2">
              Live Updates
            </Typography>
          </div>
        </div>
      )}
      
      {useSSEForOrders && !isConnected && (
        <div className="flex pl-2 items-center">
          <div className="flex items-center px-4">
            <span className="relative flex h-3 w-3 mr-2">
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
            </span>
            <Typography variant="body1" className="text-red-700 font-medium px-2">
              Disconnected
            </Typography>
          </div>
        </div>
      )}
      
      <div className="flex pl-2 w-[200px] items-center">
        <FormControlLabel
          control={
            <Checkbox
              checked={showCompletedOrders}
              onChange={(e) => onToggleCompletedOrders?.(e.target.checked)}
              size="small"
            />
          }
          label="Show completed/cancelled orders"
          sx={{ 
            '& .MuiFormControlLabel-label': { 
              fontSize: '0.875rem',
              whiteSpace: 'nowrap' 
            } 
          }}
        />
      </div>
      
      <div className="flex pl-2 w-auto">
        <button
          className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200"
          onClick={onSearchButtonClicked}
          title="Search orders">
          <MagnifyingGlassIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
    {currentCell?.timezone !== defaultTimezone ? (<div className='my-1'>
      <Typography variant="h6">All times are in the {currentCell?.timezone} timezone</Typography>
    </div>) : <div className='mb-2'></div>}
  </div>);
}
